import { ALL_BLOCK_PATTERNS } from "./blockPatterns";
import { stripLogisticalAvailabilityStatements } from "./schedulingContext";
import { getGamesForSkill, type NeurogresGame, type NeurogresSkill } from "./skillGameMap";

const operationalIntent = require("./operationalIntent");
const {
  classifyFactualPlanningIntent,
  classifyOperationalIntent,
} = operationalIntent;

export interface BlockMatch {
  key: string;
  category: "disqualifier" | "stopping_question" | "ego_victim" | "meta_model";
  label: string;
  skill: NeurogresSkill | null;
  matchedText: string;
}

export interface DetectedBlocksResult {
  text: string;
  matches: BlockMatch[];
  disqualifiers: BlockMatch[];
  stoppingQuestions: BlockMatch[];
  egoVictimPatterns: BlockMatch[];
  metaModelSignals: BlockMatch[];
  matchedBlocks: string[];
  recommendedSkills: NeurogresSkill[];
  recommendedGames: NeurogresGame[];
  primaryBlock: BlockMatch | null;
  hasHardBlock: boolean;
}

function normalizeText(value = ""): string {
  return String(value || "").replace(/\s+/g, " ").trim();
}

function messageToText(entry: unknown): string {
  if (!entry || typeof entry !== "object") return "";
  const message = entry as { content?: string; text?: string };
  return normalizeText(message.content || message.text || "");
}

function coerceInput(input: unknown): string {
  if (Array.isArray(input)) {
    return input
      .map((entry) => stripLogisticalAvailabilityStatements(messageToText(entry)))
      .filter(Boolean)
      .join(" ");
  }
  return stripLogisticalAvailabilityStatements(normalizeText(String(input || "")));
}

function dedupeStrings(values: string[]): string[] {
  return Array.from(new Set(values.filter(Boolean)));
}

function dedupeGames(values: NeurogresGame[]): NeurogresGame[] {
  return Array.from(new Set(values.filter(Boolean)));
}

export function detectBlocks(input: unknown): DetectedBlocksResult {
  const operationalIntent = classifyOperationalIntent(input);
  const factualPlanning = classifyFactualPlanningIntent(input);
  const classifiedInput = operationalIntent.operational
    ? operationalIntent.residualText
    : factualPlanning.factualPlanning
      ? ""
      : input;
  const text = coerceInput(classifiedInput);
  const matches: BlockMatch[] = ALL_BLOCK_PATTERNS.flatMap((entry) => {
    const next = new RegExp(entry.pattern.source, entry.pattern.flags);
    const found = text.match(next);
    if (!found) return [];
    return [{
      key: entry.key,
      category: entry.category,
      label: entry.label,
      skill: entry.skill || null,
      matchedText: normalizeText(found[0] || ""),
    }];
  });

  const disqualifiers = matches.filter((entry) => entry.category === "disqualifier");
  const stoppingQuestions = matches.filter((entry) => entry.category === "stopping_question");
  const egoVictimPatterns = matches.filter((entry) => entry.category === "ego_victim");
  const metaModelSignals = matches.filter((entry) => entry.category === "meta_model");
  const recommendedSkills = dedupeStrings(matches.map((entry) => entry.skill || "")) as NeurogresSkill[];
  const recommendedGames = dedupeGames(
    recommendedSkills.flatMap((skill) => [...getGamesForSkill(skill)])
  );

  return {
    text,
    matches,
    disqualifiers,
    stoppingQuestions,
    egoVictimPatterns,
    metaModelSignals,
    matchedBlocks: dedupeStrings(matches.map((entry) => entry.label)),
    recommendedSkills,
    recommendedGames,
    primaryBlock: matches[0] || null,
    hasHardBlock: Boolean(disqualifiers.length || stoppingQuestions.length || egoVictimPatterns.length),
  };
}
